import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getSchoolNetwork,
  deleteSchoolNetwork,
  getNetworkMetrics,
  updateSchoolNetwork,
  updateNetworkSchools,
  getAvailableSchools,
} from '@/api/school-networks';
import { createWhiteLabel, deleteWhiteLabel } from '@/api/white-labels';
import type { SchoolNetworkDetail, NetworkMetrics, AvailableSchool } from '@/types/school-network';
import type { MultiSelectOption } from '@/components/ui/multi-select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Network,
  Trash2,
  School,
  ExternalLink,
  Users,
  GraduationCap,
  BookOpen,
  Activity,
  Mail,
  UserCheck,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  Server,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEditableCard } from '@/hooks/useEditableCard';
import { EditableCardActions } from '@/components/schools/detail/EditableCardActions';

function getStatusBadgeVariant(
  status: string
): 'success' | 'warning' | 'default' | 'destructive' {
  switch (status) {
    case 'production':
      return 'success';
    case 'demo_empty':
    case 'demo_datas':
      return 'warning';
    case 'cancelled':
      return 'destructive';
    default:
      return 'default';
  }
}

type SortField = 'name' | 'status' | 'studentCount' | 'parentCount' | 'teacherCount' | 'classCount' | 'connexionCount' | 'mailCount';
type SortDirection = 'asc' | 'desc';

interface InfoFormData {
  [key: string]: unknown;
  name: string;
  description: string;
}

export function SchoolNetworkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [network, setNetwork] = useState<SchoolNetworkDetail | null>(null);
  const [metrics, setMetrics] = useState<NetworkMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // White label state for info card editing
  const [whiteLabel, setWhiteLabel] = useState<{ id: number; name: string } | null>(null);
  const [whiteLabelName, setWhiteLabelName] = useState('');
  const [showDeleteWhiteLabel, setShowDeleteWhiteLabel] = useState(false);
  const [deletingWhiteLabel, setDeletingWhiteLabel] = useState(false);

  // Schools editing state
  const [schoolsEditing, setSchoolsEditing] = useState(false);
  const [schoolsSaving, setSchoolsSaving] = useState(false);
  const [schoolsError, setSchoolsError] = useState<string | null>(null);
  const [availableSchools, setAvailableSchools] = useState<AvailableSchool[]>([]);
  const [selectedSchoolIds, setSelectedSchoolIds] = useState<number[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const isAdmin = hasRole('ROLE_ADMIN') || hasRole('ROLE_SUPERADMIN');

  const fetchNetwork = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getSchoolNetwork(parseInt(id!));
      setNetwork(data);
      setWhiteLabel(data.whiteLabel);
      setWhiteLabelName(data.whiteLabel?.name || '');
    } catch (err) {
      setError('Erreur lors du chargement du reseau');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMetrics = useCallback(async () => {
    try {
      setMetricsLoading(true);
      const data = await getNetworkMetrics(parseInt(id!));
      setMetrics(data);
    } catch (err) {
      console.error('Erreur lors du chargement des metriques:', err);
    } finally {
      setMetricsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchNetwork();
      fetchMetrics();
    }
  }, [id, fetchNetwork, fetchMetrics]);

  // --- Info card editable ---
  const infoInitialData: InfoFormData = useMemo(() => ({
    name: network?.name || '',
    description: network?.description || '',
  }), [network?.name, network?.description]);

  const infoCard = useEditableCard<InfoFormData>({
    initialData: infoInitialData,
    onSave: async (data) => {
      let whiteLabelId: number | undefined | null = undefined;
      const whiteLabelNameTrimmed = whiteLabelName.trim();

      if (whiteLabelNameTrimmed && !whiteLabel) {
        // Create new white label
        const hostSlug = whiteLabelNameTrimmed
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        const wlResult = await createWhiteLabel({
          name: whiteLabelNameTrimmed,
          host: `${hostSlug}.ecolefutee.com`,
        });
        whiteLabelId = wlResult.id;
      } else if (whiteLabel) {
        whiteLabelId = whiteLabel.id;
      }

      await updateSchoolNetwork(parseInt(id!), {
        name: data.name.trim(),
        description: data.description.trim() || null,
        whiteLabelId,
      });
    },
    onSaved: () => fetchNetwork(),
  });

  const handleInfoStartEditing = useCallback(() => {
    setWhiteLabel(network?.whiteLabel || null);
    setWhiteLabelName(network?.whiteLabel?.name || '');
    infoCard.startEditing();
  }, [network, infoCard]);

  const handleInfoCancelEditing = useCallback(() => {
    setWhiteLabel(network?.whiteLabel || null);
    setWhiteLabelName(network?.whiteLabel?.name || '');
    infoCard.cancelEditing();
  }, [network, infoCard]);

  // --- White label delete ---
  const handleDeleteWhiteLabel = async () => {
    if (!whiteLabel) return;
    try {
      setDeletingWhiteLabel(true);
      await updateSchoolNetwork(parseInt(id!), { whiteLabelId: null });
      await deleteWhiteLabel(whiteLabel.id);
      setWhiteLabel(null);
      setWhiteLabelName('');
      setShowDeleteWhiteLabel(false);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Erreur lors de la suppression de la marque blanche');
    } finally {
      setDeletingWhiteLabel(false);
    }
  };

  // --- Schools card editable ---
  const schoolOptions: MultiSelectOption[] = useMemo(
    () =>
      availableSchools.map((school) => ({
        value: String(school.id),
        label: school.name,
        description: school.currentNetwork && !school.inNetwork
          ? `(${school.code}) — dans: ${school.currentNetwork.name}`
          : `(${school.code})`,
      })),
    [availableSchools]
  );

  const selectedSchoolValues = useMemo(
    () => selectedSchoolIds.map(String),
    [selectedSchoolIds]
  );

  const handleSchoolsChange = (values: string[]) => {
    setSelectedSchoolIds(values.map(Number));
  };

  const handleSchoolsStartEditing = async () => {
    setSchoolsEditing(true);
    setSchoolsError(null);
    try {
      setSchoolsLoading(true);
      const data = await getAvailableSchools(parseInt(id!));
      setAvailableSchools(data);
      setSelectedSchoolIds(data.filter((s) => s.inNetwork).map((s) => s.id));
    } catch (err) {
      console.error(err);
      setSchoolsError('Erreur lors du chargement des écoles disponibles');
    } finally {
      setSchoolsLoading(false);
    }
  };

  const handleSchoolsCancelEditing = () => {
    setSchoolsEditing(false);
    setSchoolsError(null);
  };

  const handleSchoolsSave = async () => {
    try {
      setSchoolsSaving(true);
      setSchoolsError(null);
      await updateNetworkSchools(parseInt(id!), selectedSchoolIds);
      setSchoolsEditing(false);
      fetchNetwork();
    } catch (err) {
      setSchoolsError(
        err instanceof Error ? err.message : 'Erreur lors de la sauvegarde'
      );
    } finally {
      setSchoolsSaving(false);
    }
  };

  // --- Sorting & delete ---
  const handleDelete = async () => {
    if (!network) return;
    try {
      setDeleting(true);
      await deleteSchoolNetwork(network.id);
      navigate('/school-networks');
    } catch (err) {
      console.error(err);
      setError('Erreur lors de la suppression du reseau');
      setDeleting(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedSchools = useMemo(() => {
    if (!metrics?.schools) return [];

    return [...metrics.schools].sort((a, b) => {
      let comparison = 0;

      if (sortField === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortField === 'status') {
        comparison = a.status.localeCompare(b.status);
      } else {
        const aValue = a.metrics?.[sortField] ?? 0;
        const bValue = b.metrics?.[sortField] ?? 0;
        comparison = aValue - bValue;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [metrics?.schools, sortField, sortDirection]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3.5 w-3.5 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 ml-1" />
      : <ArrowDown className="h-3.5 w-3.5 ml-1" />;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <Skeleton className="h-8 w-64" />
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    );
  }

  if (!network) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Reseau non trouve</p>
        <Button asChild className="mt-4">
          <Link to="/school-networks">Retour aux reseaux</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon-sm" asChild>
              <Link to="/school-networks">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Network className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                {network.name}
              </h1>
              <p className="text-sm text-muted-foreground">
                {network.schoolCount} école{network.schoolCount !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="ghost"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">
          {error}
        </div>
      )}

      {/* KPIs globaux */}
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
                <GraduationCap className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.studentCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Élèves</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.parentCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Parents</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 text-purple-600">
                <UserCheck className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.teacherCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Enseignants</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
                <BookOpen className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.classCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Classes</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100 text-cyan-600">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.connexionCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Connexions</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100 text-pink-600">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {metricsLoading ? '-' : (metrics?.totals.mailCount ?? 0).toLocaleString('fr-FR')}
                </p>
                <p className="text-xs text-muted-foreground">Mails envoyes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Network info */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Informations - 1/3 */}
        <Card>
          <CardHeader
            actions={
              <EditableCardActions
                isEditing={infoCard.isEditing}
                isSaving={infoCard.isSaving}
                onEdit={handleInfoStartEditing}
                onSave={infoCard.save}
                onCancel={handleInfoCancelEditing}
              />
            }
          >
            <CardTitle className="text-base">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {infoCard.error && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm">
                {infoCard.error}
              </div>
            )}

            {infoCard.isEditing ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="info-name">
                    Nom <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="info-name"
                    value={infoCard.formData.name}
                    onChange={(e) => infoCard.updateField('name', e.target.value)}
                    placeholder="Ex: Reseau Paris Est"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="info-description">Description</Label>
                  <Textarea
                    id="info-description"
                    value={infoCard.formData.description}
                    onChange={(e) => infoCard.updateField('description', e.target.value)}
                    placeholder="Description optionnelle du reseau..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" />
                    Marque blanche
                  </Label>
                  {whiteLabel ? (
                    <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
                      <div>
                        <p className="font-medium text-sm">{whiteLabel.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Marque blanche associee
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setShowDeleteWhiteLabel(true)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Input
                        value={whiteLabelName}
                        onChange={(e) => setWhiteLabelName(e.target.value)}
                        placeholder="Ex: Ma Marque Blanche"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Laissez vide si vous ne souhaitez pas associer de marque blanche
                      </p>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nom</p>
                  <p className="text-foreground">{network.name}</p>
                </div>
                {network.description && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Description
                    </p>
                    <p className="text-foreground">{network.description}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-muted-foreground">
                    Date de creation
                  </p>
                  <p className="text-foreground">
                    {network.createdAt
                      ? new Date(network.createdAt).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                        })
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                    <Tag className="h-3.5 w-3.5" />
                    Marque blanche
                  </p>
                  {network.whiteLabel ? (
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 mt-1">
                      {network.whiteLabel.name}
                    </Badge>
                  ) : (
                    <p className="text-muted-foreground text-sm">Aucune</p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Écoles du réseau - 2/3 */}
        <Card className="lg:col-span-2">
          <CardHeader
            actions={
              <EditableCardActions
                isEditing={schoolsEditing}
                isSaving={schoolsSaving}
                onEdit={handleSchoolsStartEditing}
                onSave={handleSchoolsSave}
                onCancel={handleSchoolsCancelEditing}
              />
            }
          >
            <CardTitle className="text-base flex items-center gap-2">
              <School className="h-4 w-4" />
              Écoles du réseau ({network.schoolCount})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {schoolsError && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-700 text-sm mb-4">
                {schoolsError}
              </div>
            )}

            {schoolsEditing ? (
              schoolsLoading ? (
                <Skeleton className="h-12" />
              ) : (
                <MultiSelect
                  options={schoolOptions}
                  selected={selectedSchoolValues}
                  onChange={handleSchoolsChange}
                  placeholder="Sélectionner des écoles..."
                  searchPlaceholder="Rechercher une école..."
                  emptyMessage="Aucune école trouvée"
                />
              )
            ) : network.schools.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucune école dans ce réseau
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {network.schools.map((school) => (
                  <Link
                    key={school.id}
                    to={`/schools/${school.code}/edit`}
                    className="group"
                  >
                    <Card className="h-full transition-all hover:shadow-md hover:border-primary/50">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Logo */}
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted overflow-hidden">
                            {school.logoUrl ? (
                              <img
                                src={school.logoUrl}
                                alt={school.name}
                                className="h-full w-full object-contain"
                              />
                            ) : (
                              <School className="h-6 w-6 text-muted-foreground" />
                            )}
                          </div>
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate group-hover:text-primary">
                              {school.name}
                            </p>
                            <p className="font-mono text-xs text-muted-foreground">
                              {school.code}
                            </p>
                            <div className="flex flex-wrap items-center gap-1 mt-2">
                              {school.hasV1 && (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  <Server className="h-3 w-3 mr-1" />
                                  V1 — {school.v1StatusLabel || school.statusLabel}
                                </Badge>
                              )}
                              {school.hasV2 && (
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  <Server className="h-3 w-3 mr-1" />
                                  V2 — {school.v2StatusLabel || '-'}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* KPIs par école */}
      {metrics && metrics.schools.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Métriques par école</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th
                      className="text-left py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('name')}
                    >
                      <span className="flex items-center">
                        École
                        <SortIcon field="name" />
                      </span>
                    </th>
                    <th
                      className="text-left py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('status')}
                    >
                      <span className="flex items-center">
                        Statut
                        <SortIcon field="status" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('studentCount')}
                    >
                      <span className="flex items-center justify-center">
                        Élèves
                        <SortIcon field="studentCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('parentCount')}
                    >
                      <span className="flex items-center justify-center">
                        Parents
                        <SortIcon field="parentCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('teacherCount')}
                    >
                      <span className="flex items-center justify-center">
                        Enseignants
                        <SortIcon field="teacherCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('classCount')}
                    >
                      <span className="flex items-center justify-center">
                        Classes
                        <SortIcon field="classCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('connexionCount')}
                    >
                      <span className="flex items-center justify-center">
                        Connexions
                        <SortIcon field="connexionCount" />
                      </span>
                    </th>
                    <th
                      className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted/50 select-none"
                      onClick={() => handleSort('mailCount')}
                    >
                      <span className="flex items-center justify-center">
                        Mails
                        <SortIcon field="mailCount" />
                      </span>
                    </th>
                    <th className="py-2 px-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSchools.map((school) => (
                    <tr key={school.id} className="border-b last:border-0 hover:bg-muted/50">
                      <td className="py-2 px-3">
                        <div className="flex flex-col">
                          <span className="font-medium">{school.name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{school.code}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant={getStatusBadgeVariant(school.status)}>
                          {school.statusLabel}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.studentCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.parentCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.teacherCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.classCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.connexionCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3 text-center font-mono">
                        {school.metrics?.mailCount?.toLocaleString('fr-FR') ?? '-'}
                      </td>
                      <td className="py-2 px-3">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          asChild
                          title="Voir l'école"
                        >
                          <Link to={`/schools/${school.code}/edit`}>
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-muted/50 font-medium">
                    <td className="py-2 px-3" colSpan={2}>Total</td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.studentCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.parentCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.teacherCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.classCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.connexionCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3 text-center font-mono">
                      {metrics.totals.mailCount.toLocaleString('fr-FR')}
                    </td>
                    <td className="py-2 px-3"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete network confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer le reseau ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer le reseau{' '}
              <strong>{network.name}</strong> ?
              <br />
              <br />
              Les écoles associées ne seront pas supprimées, mais seront retirées du
              réseau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete white label confirmation dialog */}
      <AlertDialog open={showDeleteWhiteLabel} onOpenChange={setShowDeleteWhiteLabel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la marque blanche ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimer la marque blanche{' '}
              <strong>{whiteLabel?.name}</strong> ?
              <br />
              <br />
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingWhiteLabel}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWhiteLabel}
              disabled={deletingWhiteLabel}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {deletingWhiteLabel ? 'Suppression...' : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
